/**
 * Light, dark, and "whatever this machine says".
 *
 * ## Three states, not two
 *
 * A two-state switch has to pick a side the first time somebody opens the app,
 * and whichever side it picks is wrong for half of them. The third state —
 * System — is the DEFAULT and the honest one: the operating system already
 * knows whether this person reads on a dark screen at night, and following it
 * costs a media query. Light and Dark are the override, for the person whose
 * laptop is set one way and who wants Nexus the other.
 *
 * ## Where the decision is applied
 *
 * On `<html>`, as `data-theme`, and NOWHERE else. System removes the attribute
 * entirely rather than writing `data-theme="system"`, because "system" is not
 * a palette — it is the absence of an override, and the absence of an override
 * is exactly what `@media (prefers-color-scheme: light)` already means. The
 * stylesheet is then three blocks and no JavaScript: a dark `:root`, a media
 * query scoped `:not([data-theme='dark'])`, and `[data-theme='light']`.
 *
 * ## Why this file is duplicated in index.html
 *
 * The attribute has to be on `<html>` BEFORE the first paint, or a person who
 * chose Light sees a full dark frame while the bundle downloads and parses —
 * the flash every theme switcher is judged by. React cannot be early enough;
 * nothing that arrives as a module can. So the eight lines that read the
 * stored value and stamp the attribute are inlined in `index.html`, ahead of
 * everything, and this module is what the running app uses. The two agree on
 * one thing — `THEME_STORAGE_KEY` and the two attribute values — and
 * `test/theme.test.tsx` reads `index.html` and asserts they still do.
 */

export type ThemeChoice = 'system' | 'light' | 'dark';

/** What actually gets painted once System has been resolved. */
export type ResolvedTheme = 'light' | 'dark';

/** The one key. Also written, literally, in `index.html`'s inline script. */
export const THEME_STORAGE_KEY = 'nexus-theme';

export const THEME_CHOICES: readonly ThemeChoice[] = ['system', 'light', 'dark'];

/** The `<meta name="theme-color">` for each side: the page's own ground. */
const THEME_COLOR: Record<ResolvedTheme, string> = {
  dark: '#050816',
  light: '#f4f7fb',
};

function isChoice(value: unknown): value is ThemeChoice {
  return value === 'system' || value === 'light' || value === 'dark';
}

/**
 * What was chosen last time, or System.
 *
 * Every read is in a try/catch. `localStorage` is not a guaranteed API: a
 * private window, a browser set to block site data, and an embedded webview
 * all THROW on the getter rather than returning null, and a theme preference
 * is not worth a blank page. Anything unreadable, missing or unrecognised is
 * System, which is the same answer a first visit gets.
 */
export function readStoredTheme(): ThemeChoice {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isChoice(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

/** Remember it, or do not — a failed write is never worth an error. */
export function storeTheme(choice: ThemeChoice): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, choice);
  } catch {
    /* A browser that refuses storage still gets the theme for this session. */
  }
}

/** Does this machine ask for light? */
export function systemPrefersLight(): boolean {
  if (typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-color-scheme: light)').matches;
}

/** The choice, with System turned into the side it currently resolves to. */
export function resolveTheme(choice: ThemeChoice): ResolvedTheme {
  if (choice === 'light' || choice === 'dark') return choice;
  return systemPrefersLight() ? 'light' : 'dark';
}

/**
 * Stamp the choice on `<html>` and move the address-bar colour with it.
 *
 * The `theme-color` meta is the one piece of the theme that lives outside the
 * stylesheet: it paints the browser's own chrome on a phone, and a dark bar
 * over a light page is the detail that makes a light theme look unfinished.
 */
export function applyTheme(choice: ThemeChoice): void {
  const root = document.documentElement;
  if (choice === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', choice);

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', THEME_COLOR[resolveTheme(choice)]);
}

/**
 * Read the stored choice and put it on `<html>` at start-up.
 *
 * The inline script in `index.html` has already done this before the first
 * paint; this is the belt to its braces, and it covers the one thing the
 * inline script cannot be relied on for — any build that ever serves a
 * different HTML shell. It is a no-op when the two agree.
 */
export function initTheme(): void {
  applyTheme(readStoredTheme());
}
